/**
 * El ranking del club, una categoría a la vez.
 *
 * UNA CATEGORÍA A LA VEZ y no todas juntas porque un ranking mezclado no
 * significa nada: los puntos de «C masculino» y los de «Open femenino» salen
 * de torneos distintos contra gente distinta. La categoría es el filtro, como
 * pidió Javier, y por eso es lo primero que se elige.
 *
 * Los puntos vienen calculados del servidor. Esta pantalla no suma nada: si
 * sumara, el día que cambie la regla habría dos rankings y el que la gente ve
 * no sería el que manda.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Paginador, usePaginado } from './Paginado';
import {
  categoryKey,
  RANKING_WINDOW_DAYS,
  type Community,
  type CommunityCategory,
  type RankingRow,
} from '@tennisup/contracts';

import { Alert } from './Alert';
import { Avatar } from './Avatar';
import { Select } from './Select';
import { api, assetUrl } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';
import { categoryName } from '../lib/categoryName';

export function RankingSection({ community }: { community: Community }): JSX.Element | null {
  const { t } = useTranslation();
  /*
   * SOLO LAS TABLAS DE SINGLES, POR AHORA.
   *
   * Una tabla del ranking es de GENTE, y el servidor la pide por el sexo de la
   * persona: en el dobles mixto no existe una tabla «mixta» a la que ponerle
   * los puntos —cada quien suma en la suya— así que ofrecer ese cuadro acá es
   * ofrecer un botón que contesta «Elegí masculino o femenino» y no se puede
   * obedecer.
   *
   * Las tablas de dobles están pendientes. Está escrito y no deducido para que
   * el día que existan se vea que acá había una decisión.
   */
  const tablas = community.categories.filter((c) => c.discipline === 'SINGLES');
  const [category, setCategory] = useState<CommunityCategory | null>(tablas[0] ?? null);
  const [rows, setRows] = useState<RankingRow[] | null>(null);
  // DE A DIEZ. Un ranking de club son todos los socios de esa categoría, y en
  // un teléfono ochenta filas son catorce pantallas. `reiniciarCon` es la
  // categoría: cambiarla trae OTRO ranking, y quedarse en la página 3 del
  // anterior no significa nada.
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (category === null) return;
    setRows(null);
    setError(null);
    try {
      // `category.sex` acá es seguro porque `tablas` descartó el mixto: la
      // tabla se pide por el sexo de la persona, y en singles son el mismo.
      setRows(
        await api.ranking(
          community.id,
          category.level,
          category.sex === 'MIXED' ? 'MALE' : category.sex,
          category.discipline,
        ),
      );
    } catch (caught) {
      setError(errorMessage(t, caught));
      setRows([]);
    }
  }, [community.id, category, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Sin categorías no hay ranking posible, y un título con un «no hay nada»
  // debajo es ruido para quien solo vino a mirar el club.
  const pagina = usePaginado(
    rows ?? [],
    category === null ? '' : categoryKey(category),
  );

  if (tablas.length === 0 || category === null) return null;

  return (
    <section>
      <div className="section-head">
        <h2>{t('ranking.title')}</h2>
      </div>
      <p className="help">{t('ranking.help', { days: RANKING_WINDOW_DAYS })}</p>

      <div className="field">
        <label htmlFor="ranking-category">{t('ranking.category')}</label>
        <Select
          id="ranking-category"
          value={categoryKey(category)}
          options={tablas.map((c) => ({
            value: categoryKey(c),
            label: categoryName(t, c),
          }))}
          onChange={(clave) => {
            const elegida = tablas.find((c) => categoryKey(c) === clave);
            if (elegida !== undefined) setCategory(elegida);
          }}
        />
      </div>

      {error !== null && <Alert kind="error">{error}</Alert>}

      {rows === null ? (
        <p className="help">{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p className="help">{t('ranking.empty')}</p>
      ) : (
        <>
        <div className="table-scroll">
          <table className="ranking-table">
            <caption className="visually-hidden">
              {t('ranking.title')} · {t(`categoryLevel.${category.level}`)}
            </caption>
            <thead>
              <tr>
                <th scope="col" className="num">
                  {t('ranking.position')}
                </th>
                <th scope="col">{t('ranking.player')}</th>
                <th scope="col" className="num">
                  {t('ranking.tournaments')}
                </th>
                <th scope="col" className="num">
                  {t('ranking.points')}
                </th>
              </tr>
            </thead>
            <tbody>
              {pagina.visibles.map((row) => (
                <tr key={row.userId}>
                  <td className="num">{row.position}</td>
                  <th scope="row">
                    <span className="ranking-player">
                      <Avatar url={assetUrl(row.avatarPath)} name={row.displayName} size={32} />
                      {row.displayName}
                    </span>
                  </th>
                  <td className="num">{row.tournaments}</td>
                  <td className="num">
                    {row.points}
                    {/* DE DÓNDE SALEN, cuando parte salió de retos. Solo si
                        hay: un «0 de retos» en casi todas las filas sería
                        ruido en la única columna que la gente lee. */}
                    {row.challengePoints > 0 && (
                      <span className="help ranking-from-challenges">
                        {t('ranking.fromChallenges', { count: row.challengePoints })}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Fuera de `.table-scroll`: esa caja se desplaza de lado cuando la
            tabla no entra, y los botones de página no tienen por qué irse de
            la pantalla con ella. */}
        <Paginador pagina={pagina.pagina} paginas={pagina.paginas} irA={pagina.irA} />
        </>
      )}
    </section>
  );
}

/**
 * El aviso de quién jugó por encima de su categoría.
 *
 * Sus puntos suman en SU categoría —así el ranking de B no se llena de gente
 * de C— pero el club tiene que enterarse: quien llega lejos jugando arriba es
 * justo a quien hay que subir. Por eso el aviso trae hasta dónde llegó y no
 * solo que jugó.
 */
export function PlayedUpNotices({ communityId }: { communityId: string }): JSX.Element | null {
  const { t } = useTranslation();
  const [avisos, setAvisos] = useState<Array<Awaited<ReturnType<typeof api.playedUp>>[number]>>([]);

  useEffect(() => {
    let vivo = true;
    api
      .playedUp(communityId)
      // Si falla, no se muestra nada. Es un aviso de cortesía: llenar la
      // pantalla de un error rojo por esto sería peor que no mostrarlo.
      .then((lista) => {
        if (vivo) setAvisos(lista);
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [communityId]);

  if (avisos.length === 0) return null;

  return (
    <Alert kind="info">
      <strong>{t('playedUp.title')}</strong>
      <ul className="played-up-list">
        {avisos.map((aviso) => (
          <li key={`${aviso.userId}/${aviso.tournamentId}/${aviso.playedLevel}`}>
            {t('playedUp.line', {
              name: aviso.displayName,
              assigned: t(`categoryLevel.${aviso.assignedLevel}`),
              played: t(`categoryLevel.${aviso.playedLevel}`),
              tournament: aviso.tournamentName,
            })}{' '}
            {aviso.reachedRound === 1
              ? t('playedUp.won')
              : aviso.reachedRound === 2
                ? t('playedUp.final')
                : aviso.reachedRound > 0
                  ? t('playedUp.reached', { n: aviso.reachedRound })
                  : ''}
          </li>
        ))}
      </ul>
      <p className="help">{t('playedUp.help')}</p>
    </Alert>
  );
}
